import { Blueprint } from '../../../models/blueprint.model';
import { BlueprintItem } from '../../../models/blueprint-item.model';

export const CIRCULAR_BLUEPRINT_REFERENCE_MESSAGE =
  'This would create a circular reference. Choose a blueprint that does not include the current one.';

const stringId = (id: string | number | undefined | null): string =>
  id == null ? '' : String(id);

/**
 * True when linking linkedBlueprintId from contextBlueprint would make
 * contextBlueprint reachable from itself. Unknown ids are left for the API.
 */
export function linkedBlueprintCreatesCycle(
  contextBlueprint: Blueprint | null,
  allBlueprints: Blueprint[],
  linkedBlueprintId: string | number | null | undefined,
): boolean {
  if (!contextBlueprint) return false;
  const linkedId = stringId(linkedBlueprintId);
  if (!linkedId) return false;
  if (linkedId === stringId(contextBlueprint.id)) return true;

  const knownIds = new Set(
    allBlueprints.map((blueprint) => stringId(blueprint.id)),
  );
  if (!knownIds.has(linkedId)) return false;

  const allowedIds = new Set(
    getBlueprintsAllowedToAdd(contextBlueprint, allBlueprints).map(
      (blueprint) => stringId(blueprint.id),
    ),
  );
  return !allowedIds.has(linkedId);
}

/**
 * Blueprints safe to add when editing a context blueprint: excludes self and any
 * blueprint that references the context (directly or indirectly). When not editing, returns all.
 */
export function getBlueprintsAllowedToAdd(
  contextBlueprint: Blueprint | null,
  allBlueprints: Blueprint[],
): Blueprint[] {
  if (!contextBlueprint || !allBlueprints.length) return allBlueprints;
  const excluded = excludedBlueprintIds(
    stringId(contextBlueprint.id),
    allBlueprints,
  );
  return allBlueprints.filter((bp) => !excluded.has(stringId(bp.id)));
}

function excludedBlueprintIds(
  contextId: string,
  allBlueprints: Blueprint[],
  visited: Set<string> = new Set(),
): Set<string> {
  const result = new Set<string>([contextId]);
  if (visited.has(contextId)) return result;
  visited.add(contextId);
  for (const bp of allBlueprints) {
    if (stringId(bp.id) === contextId) continue;
    if (
      blueprintReferencesTarget(bp, contextId, allBlueprints, new Set(visited))
    ) {
      result.add(stringId(bp.id));
      excludedBlueprintIds(stringId(bp.id), allBlueprints, visited).forEach(
        (id) => result.add(id),
      );
    }
  }
  return result;
}

/**
 * Returns true if this blueprint's items reference the target blueprint (directly via
 * linked_to_blueprint_id or indirectly by following linked blueprints and their item trees).
 * Uses visited to avoid infinite recursion when blueprints reference each other.
 */
function blueprintReferencesTarget(
  blueprint: Blueprint,
  targetId: string,
  allBlueprints: Blueprint[],
  visited: Set<string>,
): boolean {
  if (visited.has(stringId(blueprint.id))) return false;
  visited.add(stringId(blueprint.id));
  if (!blueprint.items?.length) return false;
  const items = blueprint.items;
  const check = (list: BlueprintItem[]): boolean =>
    list.some((item) => {
      const linkedId = stringId(item.linked_to_blueprint_id);
      if (linkedId && linkedId === targetId) return true;
      if (linkedId) {
        const ref = allBlueprints.find((b) => stringId(b.id) === linkedId);
        if (
          ref &&
          blueprintReferencesTarget(
            ref,
            targetId,
            allBlueprints,
            new Set(visited),
          )
        )
          return true;
      }
      const children = items.filter(
        (c) => stringId(c.parent_blueprint_item_id) === stringId(item.id),
      );
      return children.length > 0 && check(children);
    });
  return check(items);
}
